<template>
	<main>
		<HeaderTab :title="page_title" />

		<div id="content">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12">
						<div class="box">
							<LoadingSpinner v-if="loading" />

							<!-- Le sens du mouvement laisse deviner qui il concerne, il ne le prouve pas : un
							     encaissement peut être l'avoir d'un fournisseur, une dépense le remboursement
							     d'un client. Le choix est donc proposé dans les deux sens, positionné sur ce
							     que le signe laisse présumer. -->
							<b-dropdown v-else-if="bridge_transaction" lazy variant="primary" class="bank-flow-justify mb-3">
								<template v-slot:button-content>
									{{ mode === 'supplier' ? $t('invoice.bank_flow_justify_supplier') : $t('invoice.bank_flow_justify_client') }}
								</template>

								<b-dropdown-item :active="mode === 'supplier'" @click.prevent="mode = 'supplier'">
									{{ $t('invoice.bank_flow_justify_supplier') }}
								</b-dropdown-item>
								<b-dropdown-item :active="mode === 'client'" @click.prevent="mode = 'client'">
									{{ $t('invoice.bank_flow_justify_client') }}
								</b-dropdown-item>
							</b-dropdown>

							<AssignationTransactionSupplier
								v-if="supplier_flow"
								:bridge_transaction="bridge_transaction"
								:initial_search="initial_search"
								@done="backToList"
							/>

							<AssignationTransaction
								v-else-if="bridge_transaction && !loading"
								:bridge_transaction="bridge_transaction"
								:initial_search="initial_search"
								@done="backToList"
							/>

							<!-- Rattaché à un règlement dont la facture a disparu. L'annulation est proposée ici :
							     l'utilisateur voit le blocage, il doit pouvoir le lever sans partir le chercher
							     dans un autre écran. -->
							<template v-else-if="orphan">
								<p>{{ $t('invoice.bank_flow_transaction_orphan') }}</p>

								<button v-if="orphan_payment_id" class="btn btn-primary" :disabled="cancelling" @click="cancelOrphanPayment">
									{{ $t('invoice.bank_flow_transaction_orphan_cancel') }}
									<font-awesome-icon v-if="cancelling" :icon="['fal', 'spinner']" spin />
								</button>
							</template>

							<p v-else-if="!loading" class="mb-0">{{ $t('invoice.bank_flow_transaction_not_found') }}</p>
						</div>

						<!-- Note libre sur le mouvement, hors de tout rapprochement : on doit pouvoir
						     expliquer une dépense qu'on ne sait pas encore justifier, ou qu'on ne
						     justifiera jamais. Elle vaut donc pour les deux parcours. -->
						<div v-if="bridge_transaction" class="box">
							<label for="bridgetransaction_comment">{{ $t('invoice.bank_flow_comment') }}</label>
							<textarea
								id="bridgetransaction_comment"
								v-model="comment"
								class="form-control"
								rows="3"
								:placeholder="$t('invoice.bank_flow_comment_placeholder')"
							></textarea>

							<button class="btn btn-primary mt-2" :disabled="saving_comment || comment === saved_comment" @click="saveComment">
								{{ $t('global.enregistrer') }}
								<font-awesome-icon v-if="saving_comment" :icon="['fal', 'spinner']" spin />
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</main>
</template>


<script type="text/javascript">
	import OpenBank from "@/mixins/OpenBank.js"
	import Payment from "@/mixins/Payment.js"

	export default {
		name: 'BankFlowTransaction',
		mixins: [OpenBank, Payment],
		props: ['transaction_id'],

		data() {
			return {
				loading: true,
				bridge_transaction: null,
				// Mouvement rattaché à une écriture dont il ne reste rien à montrer.
				orphan: false,
				orphan_payment_id: null,
				cancelling: false,
				// Parcours de justification retenu : 'supplier' ou 'client'.
				mode: null,
				comment: '',
				// Dernière note enregistrée, pour ne proposer l'enregistrement qu'en cas de changement.
				saved_comment: '',
				saving_comment: false
			}
		},

		mounted() {
			this.init_component()
		},

		computed: {
			/**
			 * L'entête porte le titre, et non le contenu : les deux parcours n'ont pas le même nom, et
			 * le mouvement n'est pas encore chargé à l'ouverture de la page.
			 */
			page_title() {
				if(!this.bridge_transaction) {
					return this.$t('invoice.bank_flow')
				}

				return this.supplier_flow ? this.$t('invoice.bank_flow_supplier') : this.$t('invoice.assignation_transaction')
			},

			/**
			 * Un débit se traite par le parcours fournisseur, un crédit par le parcours client : ils ne
			 * présentent ni les mêmes écritures ni les mêmes gestes.
			 */
			supplier_flow() {
				return this.mode === 'supplier'
			},

			/**
			 * Texte que l'utilisateur avait surligné dans le libellé du mouvement avant d'ouvrir le
			 * rapprochement : il désigne le fournisseur ou la facture, et amorce la recherche.
			 */
			initial_search() {
				return this.$route.query.q || ''
			}
		},

		methods: {
			async init_component() {
				this.loading = true
				const transaction = await this.getBridgeTransactionById(this.transaction_id)

				// Un mouvement déjà rattaché ne se rapproche plus : son adresse mène à l'écriture qui le
				// porte, là où il y a quelque chose à voir. Le renvoi est immédiat, sans afficher un
				// écran de rapprochement dont tous les gestes seraient refusés.
				if(transaction && transaction.bridgetransaction_assigned) {
					this.redirectToWriting(transaction)
					return
				}

				this.comment = (transaction && transaction.bridgetransaction_comment) || ''
				this.saved_comment = this.comment

				this.bridge_transaction = transaction
				// Le signe ne fait que présumer : une dépense règle le plus souvent un fournisseur, un
				// encaissement vient le plus souvent d'un client. L'utilisateur corrige au besoin.
				this.mode = transaction && transaction.bridgetransaction_amount < 0 ? 'supplier' : 'client'
				this.loading = false
			},

			/**
			 * Mène à l'écriture qui porte le mouvement : la facture qu'un règlement fournisseur solde,
			 * ou la fiche du tiers pour un encaissement comme pour une avance en compte courant, qui
			 * n'a pas de facture à montrer.
			 *
			 * @param {Object} transaction
			 */
			redirectToWriting(transaction) {
				if(transaction.bridgetransactionable_type === 'App\\Model\\ISPayment' && transaction.invoices && transaction.invoices[0].id) {
					this.$router.replace({ name: 'invoiceSupplierFichePayment', params: { invoicesupplier_id: transaction.invoices[0].id } })
					return
				}

				// Une remise de virements paie plusieurs fournisseurs : elle se consulte, et se défait,
				// depuis l'écran des lots, qui la relie à son fichier.
				if(transaction.bridgetransactionable_type === 'App\\Model\\SepaTransfer') {
					this.$router.replace({ name: 'invoiceSupplierSepaTransfers' })
					return
				}

				if(transaction.tiers) {
					this.$router.replace({ name: 'tiersPayment', params: { tiers_id: transaction.tiers.tiers_id } })
					return
				}

				// Rattaché, mais à une écriture qui ne montre plus rien : ses factures ont été supprimées
				// sans que le règlement le soit. Le dire plutôt que renvoyer sur la liste sans un mot,
				// où l'utilisateur recliquerait sur le même mouvement.
				// Seul un règlement fournisseur s'annule d'ici : les encaissements et les remises de
				// chèques se défont dans leurs propres écrans, qui savent ce qu'ils emportent avec eux.
				if(transaction.bridgetransactionable_type === 'App\\Model\\ISPayment') {
					this.orphan_payment_id = transaction.bridgetransactionable_id
				}

				this.orphan = true
				this.loading = false
			},

			/**
			 * Annule le règlement qui retient le mouvement, puis relit la page : le flux est alors libre,
			 * et le rapprochement s'ouvre sur les propositions.
			 */
			async cancelOrphanPayment() {
				this.cancelling = true
				await this.cancelPaymentSupplier([this.orphan_payment_id])
				this.cancelling = false

				this.successToast('toast.info_save_succes')
				this.orphan = false
				this.orphan_payment_id = null
				this.init_component()
			},

			async saveComment() {
				this.saving_comment = true
				await this.saveBridgeTransactionComment(this.transaction_id, this.comment)
				this.saving_comment = false

				this.saved_comment = this.comment
				this.successToast('toast.info_save_succes')
			},

			/**
			 * Rend la liste des flux sur la période qu'elle montrait : la liste l'a confiée à
			 * l'adresse du mouvement en l'ouvrant. Arrivé par un lien qui ne la porte pas, la liste
			 * reprend sa période par défaut.
			 */
			backToList() {
				const { start, end } = this.$route.query
				this.$router.push({ name: 'bankFlow', query: start && end ? { start, end } : {} })
			}
		},

		components: {
			HeaderTab: () => import('@/components/HeaderTab'),
			AssignationTransaction: () => import('@/components/Invoice/AssignationTransaction'),
			AssignationTransactionSupplier: () => import('@/components/Invoice/AssignationTransactionSupplier'),
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
	}
</script>
